import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { EMPTY, Observable, of } from 'rxjs';
import { expand, map, reduce } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import {
  IRaffle,
  IRafflesMetadata,
  IRaffleConditionRaffle,
  IRaffleConditionsRaffleMetadata,
  IRaffleRewardRaffle,
  IRaffleRewardsRaffleMetadata,
  IUserRaffleCondition,
  IUserRaffleConditionMetadata,
  IUserRaffleMutationResult,
} from './raffle.interfaces';

@Injectable({ providedIn: 'root' })
export class RafflesService {
  private readonly url = environment.GATEWAY_URL + '/raffle';
  constructor(private readonly http: HttpClient) {}

  getRaffles(userId: number, params: object): Observable<IRafflesMetadata> {
    return this.http.post<IRafflesMetadata>(
      this.url + '/' + userId + '/get',
      params,
    );
  }

  getConditions(raffle: IRaffle): Observable<IRaffleConditionRaffle[]> {
    return this.allPages<IRaffleConditionRaffle>(offset =>
      this.http
        .post<IRaffleConditionsRaffleMetadata>(this.url + '/conditions/get', {
          raffleId: raffle.id,
          link: raffle.link,
          pagination: { limit: 100, offset },
        })
        .pipe(
          map(page => ({ rows: page.conditions, total: page.metadata.amount })),
        ),
    );
  }

  getRewards(raffle: IRaffle): Observable<IRaffleRewardRaffle[]> {
    return this.allPages<IRaffleRewardRaffle>(offset =>
      this.http
        .post<IRaffleRewardsRaffleMetadata>(this.url + '/rewards/get', {
          raffleId: raffle.id,
          pagination: { limit: 100, offset },
        })
        .pipe(
          map(page => ({ rows: page.rewards, total: page.metadata.amount })),
        ),
    );
  }

  getUserConditions(
    userId: number,
    conditionsIds: number[],
  ): Observable<IUserRaffleCondition[]> {
    if (!conditionsIds.length) return of([]);
    return this.allPages<IUserRaffleCondition>(offset =>
      this.http
        .post<IUserRaffleConditionMetadata>(
          this.url + '/' + userId + '/conditions/get',
          {
            conditionsIds,
            pagination: { limit: 100, offset },
          },
        )
        .pipe(
          map(page => ({
            rows: page.usersRafflesConditions,
            total: page.metadata.amount,
          })),
        ),
    );
  }

  updateCondition(
    userId: number,
    raffleConditionRaffleId: number,
    conditionsCount: number,
    reason: string,
  ): Observable<IUserRaffleMutationResult> {
    return this.http.post<IUserRaffleMutationResult>(
      this.url + '/' + userId + '/update-user-raffle-condition',
      { raffleConditionRaffleId, conditionsCount, reason },
    );
  }

  deleteCondition(
    userId: number,
    raffleConditionRaffleId: number,
    reason: string,
  ): Observable<IUserRaffleMutationResult> {
    return this.http.post<IUserRaffleMutationResult>(
      this.url + '/' + userId + '/delete-user-raffle-condition',
      { raffleConditionRaffleId, reason },
    );
  }

  exclude(
    userId: number,
    raffleId: number,
    reason: string,
  ): Observable<IUserRaffleMutationResult> {
    return this.http.post<IUserRaffleMutationResult>(
      this.url + '/' + userId + '/delete-user-raffle',
      { raffleId, reason },
    );
  }

  private allPages<T extends { id: number }>(
    fetch: (offset: number) => Observable<{ rows: T[]; total: number }>,
  ): Observable<T[]> {
    return fetch(0).pipe(
      map(page => ({ ...page, offset: 0 })),
      expand(page =>
        page.rows.length && page.offset + page.rows.length < page.total
          ? fetch(page.offset + page.rows.length).pipe(
              map(next => ({
                ...next,
                offset: page.offset + page.rows.length,
              })),
            )
          : EMPTY,
      ),
      reduce((rows: T[], page) => rows.concat(page.rows), []),
      map(rows => Array.from(new Map(rows.map(row => [row.id, row])).values())),
    );
  }
}
